<script lang="ts">
    import { createEventDispatcher, onDestroy, onMount } from 'svelte'
    import dayjs from 'dayjs'
    import Zbutton from '../_common/z/zbutton.svelte'
    import LoadSpinner from '../_common/load-spinner.svelte'
    import type { GetPixDto } from '../../modules/paymentsApi/charges/get-pix.dto'
    import { ChargeStatus } from '../../modules/paymentsApi/charges/charge.enum'

    const dispatch = createEventDispatcher()

    export let chargeId: string
    // quando true, consulta o gateway até o pagamento ser confirmado (tela do cliente pagando)
    export let watchPayment: boolean = true

    // Com watchPayment a tela consulta o gateway na hora: é o único momento em que há alguém
    // esperando. Sem watchPayment (tela interna) só lê o nosso banco.
    const POLL_SECONDS = 10
    // A consulta para depois disso. Uma aba esquecida aberta consultaria o gateway por dias,
    // que é justamente o desperdício que a consulta sob demanda existe para evitar.
    const WATCH_MINUTES = 20

    let pix: GetPixDto = null
    let show = true
    let paid = false
    let copied = false
    let timer = null
    let watchExpired = false
    let startedAt = 0

    async function load(checkGateway: boolean = false) {
        try {
            pix = checkGateway ? await window.api.payments.charges.refreshPix(chargeId) : await window.api.payments.charges.getPix(chargeId)
            paid = pix?.status >= ChargeStatus.paid_manual
            if (paid) stopWatching()
        } catch (error) {
            window.error(error.message || error)
        } finally {
            show = false
        }
    }

    function stopWatching() {
        if (timer) {
            clearInterval(timer)
            timer = null
        }
    }

    function startWatching() {
        stopWatching()
        watchExpired = false
        startedAt = Date.now()
        timer = setInterval(async () => {
            if (Date.now() - startedAt > WATCH_MINUTES * 60 * 1000) {
                stopWatching()
                watchExpired = true
                return
            }
            await load(true)
        }, POLL_SECONDS * 1000)
    }

    async function checkAgain() {
        show = true
        await load(true)
        if (!paid) startWatching()
    }

    onMount(async () => {
        await load()
        if (watchPayment && !paid) startWatching()
    })

    onDestroy(stopWatching)

    async function copyCode() {
        try {
            await navigator.clipboard.writeText(pix.pixEMV)
            copied = true
            setTimeout(() => (copied = false), 2500)
        } catch (error) {
            window.error('Não foi possível copiar. Selecione o código e copie manualmente.')
        }
    }
</script>

<div class="pix-root">
    <LoadSpinner {show} />

    {#if !show}
        {#if paid}
            <div class="pix-state">
                <div class="pix-state-title">Pagamento confirmado</div>
                <div>Recebemos seu pix. Você já pode fechar esta tela.</div>
                <Zbutton label="Fechar" color="green" on:click={() => dispatch('close')}></Zbutton>
            </div>
        {:else if pix?.expired || !pix?.pixEMV}
            <div class="pix-state">
                <div class="pix-state-title">QR Code expirado</div>
                <div>Este código não pode mais ser pago. Gere uma nova cobrança para continuar.</div>
                <Zbutton label="Fechar" color="secondary" on:click={() => dispatch('close')}></Zbutton>
            </div>
        {:else}
            <div class="pix-box">
                {#if pix.pixBRCode}
                    <img class="pix-qr" src={pix.pixBRCode} alt="QR Code do pix" />
                {/if}

                <div class="pix-hint">Abra o app do seu banco, escolha pagar com pix e leia o QR Code.</div>

                <!-- lado a lado quando cabe; em tela estreita o botão desce para a linha de baixo -->
                <div class="pix-code-row">
                    <div class="pix-code" title={pix.pixEMV}>{pix.pixEMV}</div>
                    <Zbutton label={copied ? 'Copiado' : 'Copiar'} color="primary" growM={true} on:click={copyCode}></Zbutton>
                </div>

                <div class="pix-foot">
                    <!-- {#if pix.expiresAt}
                        <span>Válido até {dayjs(pix.expiresAt).format('DD/MM/YYYY [às] HH:mm')}.</span>
                    {/if} -->
                    {#if watchPayment}
                        {#if watchExpired}
                            <span class="pix-recheck">
                                <Zbutton label="Verificar pagamento" color="secondary" on:click={checkAgain}></Zbutton>
                            </span>
                        {:else}
                            <span>Aguardando o pagamento — a confirmação aparece aqui.</span>
                        {/if}
                    {/if}
                </div>
            </div>
        {/if}
    {/if}
</div>

<style>
    .pix-root {
        height: 100%;
    }
    .pix-box {
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 10px 14px 14px;
        text-align: center;
        gap: 8px;
    }
    .pix-qr {
        width: 190px;
        height: 190px;
        max-width: 100%;
        background: #fff;
        padding: 6px;
        border-radius: 8px;
    }
    .pix-hint {
        max-width: 420px;
        font-size: 14px;
    }
    .pix-code-row {
        display: flex;
        /* O botão tem min-width: fit-content e nunca encolhe, então em tela estreita ele espremia o
           código contra a borda. Com growM ele passa a ocupar a largura toda abaixo de 900px, e é
           o wrap que permite isso virar uma segunda linha em vez de estourar a largura. */
        flex-wrap: wrap;
        align-items: center;
        gap: 8px;
        width: 100%;
        max-width: 460px;
    }
    /* uma linha só, cortando o excesso: o código inteiro vai no clipboard pelo botão */
    .pix-code {
        flex: 1;
        min-width: 0;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        font-family: monospace;
        font-size: 12px;
        padding: 8px;
        border-radius: 6px;
        background: rgba(128, 128, 128, 0.14);
        text-align: left;
    }
    .pix-foot {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 4px;
        font-size: 13px;
        opacity: 0.85;
    }
    .pix-recheck {
        opacity: 1;
        margin-top: 2px;
    }
    .pix-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 10px;
        padding: 24px;
        text-align: center;
    }
    .pix-state-title {
        font-weight: 600;
        font-size: 16px;
    }
</style>
